// The hatch of unsurveyed water (v2-the-deep.md §4.1, chart card 20 as round
// 088 card 06 draws it, ui-map-interface.md §4.5, R2626).
//
// The layer owns no geometry. The engine's survey plane holds 1 on a cell
// the survey of the viewer's faction holds, and ONE full-world quad paints
// the veil and the hatch where it holds 0 (`shaders/survey.ts`). A hello
// and a full frame write the whole plane from the bitset; a delta writes
// one texel for each new cell. The plane uploads only when a write marked
// it dirty, so a frame at rest costs one quad and nothing else (§10).
//
// **IT DRAWS WHAT THE WIRE SENDS** (v2 §14.1 rule 5). The layer is visible
// while the wire carries a survey, which it does only while the flag
// `discovery` is latched. With no survey it draws nothing at all.

import type { DataTexture } from '../gfx/types';
import type { WorldEnv, WorldPass } from '../gfx/worldquad';
import { UniformValues } from '../gfx/uniforms';
import type { DrawCtx, FrameInfo, Layer } from '../layer';
import { SURVEY_HATCH } from '../../gen/presentation';
import { SURVEY_UNIFORMS, surveyProgram } from '../shaders/survey';
import { surveyBit } from '../../survey';
import type { SurveyDelta } from '../../survey';

/** What the layer needs of the engine. */
export interface SurveyEngine {
  readonly survey: DataTexture;
  readonly env: WorldEnv;
  newWorldPass(program: ReturnType<typeof surveyProgram>): WorldPass;
}

/**
 * Writes a whole bitset of the wire's encoding into a plane of one byte a
 * cell: 1 on a cell the set holds, 0 elsewhere. A null set writes 1 on
 * every cell: no survey hatches nothing.
 */
export function writeMask(plane: { data: Uint8Array | Float32Array; markDirty(): void },
  bits: Uint8Array | null): void {
  const out = plane.data;
  if (!bits) { out.fill(1); plane.markDirty(); return; }
  for (let c = 0; c < out.length; c++) out[c] = surveyBit(bits, c) ? 1 : 0;
  plane.markDirty();
}

export class SurveyLayer implements Layer {
  readonly name = 'survey';

  private readonly pass: WorldPass;
  private readonly u = new UniformValues(SURVEY_UNIFORMS);
  /** True while the wire carries a survey. */
  private on = false;
  private shown = true;

  constructor(private readonly engine: SurveyEngine) {
    this.pass = engine.newWorldPass(surveyProgram());
    this.pass.setExtra((p) => this.u.apply(p));
  }

  /** The layer draws while a survey stands and nothing hides it. */
  get visible(): boolean { return this.on && this.shown; }
  setVisible(v: boolean): void { this.shown = v; }

  /** True while the wire carries a survey. The tests read it. */
  get active(): boolean { return this.on; }

  /** The historical reading draws its lines a little stronger. */
  setReading(historical: boolean): void {
    this.u.set('uSvLineAlpha', historical ? SURVEY_HATCH.lineAlphaHistorical : SURVEY_HATCH.lineAlpha);
  }

  /** A hello: the whole plane from its bitset, or none. */
  reset(bits: Uint8Array | null): void {
    this.on = bits !== null && bits.length > 0;
    writeMask(this.engine.survey, this.on ? bits : null);
  }

  /** One State frame's change: the whole plane, or one texel a new cell. */
  apply(delta: SurveyDelta, bits: Uint8Array | null): void {
    if (delta.full) { this.reset(bits); return; }
    if (!this.on) return;
    const plane = this.engine.survey;
    const out = plane.data;
    for (const c of delta.cells) if (c >= 0 && c < out.length) out[c] = 1;
    if (delta.cells.length > 0) plane.markDirty();
  }

  update(_frame: FrameInfo): void { /* the plane changes only on a frame */ }

  draw(ctx: DrawCtx): void {
    this.pass.draw(ctx.cam, this.engine.env);
  }

  dispose(): void { this.pass.dispose(); }
}
